/**
 * ForecastAnimationService.ts
 *
 * Orchestrates multi-layer forecast timeline animation, continuous looping within
 * user-selected duration windows (8h / 24h / 48h), aggressive viewport tile preloading,
 * and double-buffered layer updates to guarantee smooth, judder-free playback.
 */

import maplibregl from 'maplibre-gl';
import store, { AppState } from '../state';
import { ProductId } from '../types';
import {
  getTileUrl,
  appendAuthToken,
  authenticatedFetch,
  isSingleImageProduct,
  getForecastSingleImageUrl,
  markSingleImageUnavailable,
  buildPmtilesUrl
} from '../config';
import { preloadPmtilesHeader, preloadPmtilesTile } from '../utils/pmtilesCache';
import imageBitmapCache from '../utils/ImageBitmapCache';
import ManifestTimelineHelper from './ManifestTimelineHelper';

export class ForecastAnimationService {
  private static instance: ForecastAnimationService;
  private map: maplibregl.Map | null = null;
  private layers: Record<string, any> = {};
  private timerId: any = null;
  private rafId: number | null = null;
  private lastFramePerfMs: number = 0;
  private lastDispatchedHourMs: number = 0;
  private preloadedUrls: Set<string> = new Set();
  private frameListeners: Set<(playheadTimeMs: number) => void> = new Set();

  private constructor() {}

  public static getInstance(): ForecastAnimationService {
    if (!ForecastAnimationService.instance) {
      ForecastAnimationService.instance = new ForecastAnimationService();
    }
    return ForecastAnimationService.instance;
  }

  public setMap(map: maplibregl.Map): void {
    this.map = map;
  }

  public setLayers(layers: Record<string, any>): void {
    this.layers = layers;
  }

  /**
   * Registers a 60 FPS continuous playhead callback invoked on every animation frame.
   */
  public onFrame(cb: (playheadTimeMs: number) => void): () => void {
    this.frameListeners.add(cb);
    return () => {
      this.frameListeners.delete(cb);
    };
  }

  /**
   * Returns the continuous floating-point playhead timestamp (ms) while playback is running.
   */
  public getCurrentCursorMs(): number | null {
    if (this.rafId !== null && this.currentCursorMs > 0) {
      return this.currentCursorMs;
    }
    return null;
  }

  /**
   * Evaluates whether any animatable forecast product layer is currently enabled.
   */
  public hasActiveForecastLayers(state: AppState): boolean {
    if (state.webcamsEnabled || state.climateSavedTimelineRange !== null) {
      return false;
    }

    const forecastProducts: ProductId[] = [
      'clouds', 'winds', 'hrtemp', 'hrtemp_contours', 'smoke', 'aqi', 'precip', 'precip_prob',
      'precip_24h', 'snow_24h', 'sun_intensity', 'valley_fog', 'windchill',
      'relative_humidity', 'dewpoint', 'freezing_level', 'freezing_level_contours',
      'optics_dew_risk', 'lenticular', 'lightning', 'thunderstorm', 'goldenhour', 'sunrise',
      'alpenglow', 'darkskies', 'cape', 'cloud_base', 'cloud_inversion', 'mountain_visibility', 'aurora', 'seeing'
    ];

    const primary = state.primaryProduct;
    if (primary && forecastProducts.includes(primary)) {
      return true;
    }

    if (
      state.cloudsEnabled ||
      state.windsEnabled ||
      state.smokeEnabled ||
      state.opticsDewRiskEnabled ||
      state.seeingEnabled ||
      state.freezingLevelFillEnabled ||
      state.auroraEnabled ||
      (state as any).darkskiesEnabled ||
      (state as any).alpenglowEnabled ||
      (state as any).sunriseEnabled ||
      (state as any).goldenhourEnabled ||
      (state as any).lenticularEnabled ||
      (state as any).precipEnabled ||
      (state as any).precipProbEnabled ||
      (state as any).precip24hEnabled ||
      (state as any).snow24hEnabled ||
      (state as any).lightningEnabled ||
      (state as any).sunIntensityEnabled ||
      (state as any).aqiEnabled ||
      (state as any).freezingLevelEnabled ||
      (state as any).relativeHumidityEnabled ||
      (state as any).windchillEnabled ||
      (state as any).cloudBaseEnabled ||
      (state as any).cloudInversionEnabled ||
      (state as any).mountainVisibilityEnabled ||
      (state as any).capeEnabled ||
      (state as any).thunderstormEnabled ||
      (state as any).valleyFogEnabled
    ) {
      return true;
    }

    if (state.activeLayerProducts) {
      for (const p of forecastProducts) {
        if (state.activeLayerProducts[p] === true) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Retrieves active forecast product identifiers and their currently selected models.
   */
  public getActiveForecastLayers(state: AppState): Array<{ product: ProductId; model: string; extra?: string }> {
    const list: Array<{ product: ProductId; model: string; extra?: string }> = [];
    const manifest = state.manifest;
    if (!manifest) return list;

    const resolveDefaultModel = (prod: ProductId): string => {
      const fromManifest = manifest.default_models?.[prod];
      if (fromManifest) return fromManifest;
      if (prod === 'hrtemp' || prod === 'hrtemp_contours') return 'skysk_hres_topo';
      if (prod === 'sunrise' || prod === 'alpenglow' || prod === 'goldenhour') return 'gw_v2_blend_50';
      if (prod === 'cloud_base' || prod === 'cloud_inversion' || prod === 'mountain_visibility' || prod === 'precip' || prod === 'precip_prob' || prod === 'precip_24h' || prod === 'snow_24h' || prod === 'winds') return 'nbm';
      if (prod === 'clouds' || prod === 'lenticular') return 'ecmwf';
      if (prod === 'smoke' || prod === 'valley_fog' || prod === 'freezing_level' || prod === 'relative_humidity' || prod === 'dewpoint' || prod === 'windchill' || prod === 'thunderstorm') return 'hrrr';
      if (prod === 'aqi') return 'gefs';
      if (prod === 'sun_intensity') return 'rrfs';
      if (prod === 'seeing' || prod === 'optics_dew_risk') return 'rdps';
      return 'blend';
    };

    const pushProduct = (rawProd: ProductId) => {
      let prod = rawProd;
      if (prod === 'precip') {
        const mode = state.precipViewMode || 'rate';
        if (mode === 'prob') prod = 'precip_prob';
        else if (mode === '24h_rain') prod = 'precip_24h';
        else if (mode === '24h_snow') prod = 'snow_24h';
      } else if (prod === 'alpenglow' && state.alpenglowMode === 'goldenhour') {
        prod = 'goldenhour';
      }

      const model =
        state.selectedModels[prod] ||
        state.selectedModels[rawProd] ||
        (state as any)[`${prod}Model`] ||
        (state as any)[`${rawProd}Model`] ||
        resolveDefaultModel(prod);

      if (prod === 'clouds') {
        const levels = state.cloudLevels && state.cloudLevels.length > 0 ? state.cloudLevels : ['total'];
        levels.forEach(lvl => {
          list.push({ product: 'clouds', model: state.selectedModels['clouds'] || state.cloudModel || model, extra: lvl });
        });
      } else if (prod === 'smoke') {
        const levels = state.smokeLevels && state.smokeLevels.length > 0 ? state.smokeLevels : ['surface'];
        levels.forEach(lvl => {
          list.push({ product: 'smoke', model: state.selectedModels['smoke'] || state.smokeModel || model, extra: lvl });
        });
      } else {
        list.push({ product: prod, model });
      }
    };

    // Check primary product
    const primary = state.primaryProduct;
    if (primary) {
      pushProduct(primary);
    }

    // Clouds
    if (state.cloudsEnabled && primary !== 'clouds') {
      pushProduct('clouds');
    }

    // Winds
    if (state.windsEnabled && primary !== 'winds') {
      pushProduct('winds');
    }

    // Smoke
    if (state.smokeEnabled && primary !== 'smoke') {
      pushProduct('smoke');
    }

    // Optics dew risk
    if (state.opticsDewRiskEnabled && primary !== 'optics_dew_risk' && !state.activeLayerProducts?.['optics_dew_risk']) {
      pushProduct('optics_dew_risk');
    }

    // Freezing level fill
    if (state.freezingLevelFillEnabled && primary !== 'freezing_level' && !state.activeLayerProducts?.['freezing_level']) {
      pushProduct('freezing_level');
    }

    // Other multi-layers
    if (state.activeLayerProducts) {
      Object.keys(state.activeLayerProducts).forEach(key => {
        const prod = key as ProductId;
        if (state.activeLayerProducts[prod] && prod !== primary && prod !== 'clouds' && prod !== 'winds' && prod !== 'smoke' && prod !== 'optics_dew_risk' && prod !== 'freezing_level') {
          pushProduct(prod);
        }
      });
    }

    return list;
  }

  /**
   * Calculates visible tile coordinates (z, x, y) for the current map viewport.
   * Clamps zoom to [0, 5] matching standard forecast tile zoom levels.
   */
  public getVisibleTileCoords(): Array<{ z: number; x: number; y: number }> {
    if (!this.map) return [];

    try {
      const bounds = this.map.getBounds();
      const rawZoom = this.map.getZoom();
      const z = Math.min(5, Math.max(0, Math.floor(rawZoom)));

      const n = Math.pow(2, z);

      let west = bounds.getWest();
      let east = bounds.getEast();
      let north = Math.min(85.0511, Math.max(-85.0511, bounds.getNorth()));
      let south = Math.min(85.0511, Math.max(-85.0511, bounds.getSouth()));

      // Clamp longitudes
      west = Math.max(-180, Math.min(180, west));
      east = Math.max(-180, Math.min(180, east));

      const minX = Math.max(0, Math.floor((west + 180) / 360 * n));
      const maxX = Math.min(n - 1, Math.floor((east + 180) / 360 * n));

      const latRadNorth = north * Math.PI / 180;
      const latRadSouth = south * Math.PI / 180;

      const minY = Math.max(0, Math.floor((1 - Math.log(Math.tan(latRadNorth) + 1 / Math.cos(latRadNorth)) / Math.PI) / 2 * n));
      const maxY = Math.min(n - 1, Math.floor((1 - Math.log(Math.tan(latRadSouth) + 1 / Math.cos(latRadSouth)) / Math.PI) / 2 * n));

      const coords: Array<{ z: number; x: number; y: number }> = [];
      const MAX_TILES = 30; // Safeguard against excessive prefetch load
      let count = 0;

      for (let x = minX; x <= maxX; x++) {
        for (let y = minY; y <= maxY; y++) {
          coords.push({ z, x, y });
          count++;
          if (count >= MAX_TILES) return coords;
        }
      }

      return coords;
    } catch {
      return [];
    }
  }

  /**
   * Generates URLs for all visible tiles for a specific timestamp and active layers.
   */
  public getTileUrlsForDate(date: Date, coords: Array<{ z: number; x: number; y: number }>): string[] {
    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return [];

    const activeLayers = this.getActiveForecastLayers(state);
    if (activeLayers.length === 0) return [];

    const dateStr = date.toISOString().split('T')[0];
    const hourStr = String(date.getUTCHours()).padStart(2, '0');
    const urls: string[] = [];

    activeLayers.forEach(({ product, model, extra }) => {
      let effectiveHour = hourStr;
      if (product === 'clouds' && extra) {
        effectiveHour = `${extra}_${dateStr}_${hourStr}UTC`;
      } else if (product === 'smoke') {
        effectiveHour = `surface_${dateStr}_Z${hourStr}`;
      } else if (product === 'aqi') {
        effectiveHour = `aqi_raqdps_${dateStr}_Z${hourStr}`;
      }

      if (isSingleImageProduct(manifest, product)) {
        const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
        const is4K = typeof window !== 'undefined' && window.innerWidth >= 2560;
        const supports4K = ['hrtemp', 'freezing_level', 'valley_fog', 'goldenwindow', 'sunrise', 'sunset', 'alpenglow', 'goldenhour'].includes(product);
        const res: 1024 | 2048 | 4096 = isMobile ? 1024 : (is4K && supports4K ? 4096 : 2048);
        const singleUrl = getForecastSingleImageUrl(manifest, product, model, dateStr, effectiveHour, res);
        if (singleUrl && !this.preloadedUrls.has(singleUrl)) {
          urls.push(singleUrl);
        }
        return;
      }

      const template = getTileUrl(manifest, product, model, dateStr, effectiveHour);
      if (!template || template.includes('.pmtiles')) {
        // PMTiles are single files, so fetching the template itself warms the cache
        if (template && !this.preloadedUrls.has(template)) {
          urls.push(template);
        }
        return;
      }

      coords.forEach(({ z, x, y }) => {
        const url = template
          .replace('{z}', String(z))
          .replace('{x}', String(x))
          .replace('{y}', String(y));

        if (!this.preloadedUrls.has(url)) {
          urls.push(url);
        }
      });
    });

    return urls;
  }

  /**
   * Pre-loads an array of tile URLs into the off-thread ImageBitmap cache or HTTP cache.
   */
  public async preloadUrls(urls: string[]): Promise<void> {
    if (urls.length === 0) return;

    const promises = urls.map(url => {
      this.preloadedUrls.add(url);
      if (url.includes('.pmtiles') || url.startsWith('pmtiles://')) {
        return preloadPmtilesHeader(url).catch(() => {});
      }
      if (url.includes('.webp') && !url.includes('{z}')) {
        imageBitmapCache.loadBoundsForWebpUrl(url).catch(() => {});
        return imageBitmapCache.load(url).then(() => {}).catch(() => {});
      }
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = url;
      });
    });

    await Promise.race([
      Promise.allSettled(promises),
      new Promise(r => setTimeout(r, 1500))
    ]);
  }

  /**
   * Pre-loads the next N hours of tiles from the given date anchor into off-thread ImageBitmap pool / CacheStorage.
   */
  public async preloadHours(anchorDate: Date, hourCount: number): Promise<void> {
    const coords = this.getVisibleTileCoords();

    const state = store.getState();
    const manifest = state.manifest;
    if (!manifest) return;

    const activeLayers = this.getActiveForecastLayers(state);
    const activeLayerInstances = Object.values(this.layers).filter(
      (layer: any) => layer && layer.active && typeof layer.getPreloadTargetsForTime === 'function'
    );

    if (activeLayers.length === 0 && activeLayerInstances.length === 0) return;

    const tasks: Array<Promise<any>> = [];

    const preloadPmtilesArchiveAndViewport = (pmtilesUrl: string) => {
      if (!pmtilesUrl) return;
      if (!this.preloadedUrls.has(pmtilesUrl)) {
        this.preloadedUrls.add(pmtilesUrl);
        tasks.push(preloadPmtilesHeader(pmtilesUrl).catch(() => {}));
      }
      coords.forEach(({ z, x, y }) => {
        const tileKey = `${pmtilesUrl}/${z}/${x}/${y}`;
        if (!this.preloadedUrls.has(tileKey)) {
          this.preloadedUrls.add(tileKey);
          tasks.push(preloadPmtilesTile(pmtilesUrl, z, x, y).catch(() => {}));
        }
      });
    };

    const preloadSingleWebpWithFallback = (singleUrl: string, product: string, layerId?: string) => {
      if (!singleUrl || this.preloadedUrls.has(singleUrl)) return;
      this.preloadedUrls.add(singleUrl);
      const pBounds = imageBitmapCache.loadBoundsForWebpUrl(singleUrl).catch(() => {});
      const pBitmap = imageBitmapCache.load(singleUrl).then(async (bmp) => {
        if (!bmp && imageBitmapCache.hasFailed(singleUrl)) {
          markSingleImageUnavailable(product);
          if (layerId) markSingleImageUnavailable(layerId);
          const clean = singleUrl.split('?')[0];
          const pmtilesUrl = buildPmtilesUrl(clean.replace(/_(?:1024|2048|4096)\.webp$/, '.pmtiles'));
          await preloadPmtilesHeader(pmtilesUrl).catch(() => {});
          await Promise.allSettled(
            coords.map(({ z, x, y }) => preloadPmtilesTile(pmtilesUrl, z, x, y).catch(() => {}))
          );
        }
      }).catch(() => {});
      tasks.push(pBounds, pBitmap);
    };

    for (let i = 0; i <= hourCount; i++) {
      const d = new Date(anchorDate.getTime() + i * 3600 * 1000);
      const dateStr = d.toISOString().split('T')[0];
      const hourStr = String(d.getUTCHours()).padStart(2, '0');

      // 1. Exact URL preloading from active BaseProduct instances (guarantees zero URL mismatch on Cycle 1)
      const handledProducts = new Set<string>();
      activeLayerInstances.forEach((layer: any) => {
        const targets = layer.getPreloadTargetsForTime(d.getTime()) || [];
        targets.forEach((target: { layerId: string; product: ProductId; model: string; url0: string; url1: string; isSingleImage: boolean }) => {
          handledProducts.add(target.product);
          const urlsToLoad = target.url1 && target.url1 !== target.url0 ? [target.url0, target.url1] : [target.url0];
          urlsToLoad.forEach((u) => {
            if (!u) return;
            if (target.isSingleImage && isSingleImageProduct(manifest, target.product, target.model)) {
              preloadSingleWebpWithFallback(u, target.product, target.layerId);
            } else if (u.includes('.pmtiles') || u.startsWith('pmtiles://')) {
              preloadPmtilesArchiveAndViewport(u);
            } else if (u.includes('{z}')) {
              coords.forEach(({ z, x, y }) => {
                const tileUrl = u.replace('{z}', String(z)).replace('{x}', String(x)).replace('{y}', String(y));
                if (!this.preloadedUrls.has(tileUrl)) {
                  this.preloadedUrls.add(tileUrl);
                  tasks.push(new Promise<void>((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve();
                    img.onerror = () => resolve();
                    img.src = tileUrl;
                  }));
                }
              });
            }
          });
        });
      });

      // 2. Fallback preloading for non-BaseProduct layers (e.g. winds wind_grid.json) or uninitialized layers
      activeLayers.forEach(({ product, model, extra }) => {
        if (handledProducts.has(product) && product !== 'winds') {
          return;
        }

        if (isSingleImageProduct(manifest, product, model)) {
          const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
          const is4K = typeof window !== 'undefined' && window.innerWidth >= 2560;
          const supports4K = ['hrtemp', 'freezing_level', 'valley_fog', 'goldenwindow', 'sunrise', 'sunset', 'alpenglow', 'goldenhour'].includes(product);
          const res: 1024 | 2048 | 4096 = isMobile ? 1024 : (is4K && supports4K ? 4096 : 2048);

          const bracket = ManifestTimelineHelper.getBracketingFrames(manifest, product, model, d);
          const candidateFrames = bracket ? [bracket.frame0, bracket.frame1] : [{ dateStr, hourStr: `Z${hourStr}`, timestamp: d.getTime() }];

          candidateFrames.forEach(frame => {
            const fDate = frame.dateStr;
            const cleanH = frame.hourStr.replace(/^Z/i, '');
            let fHour = cleanH;
            if (product === 'clouds' && extra) {
              fHour = `${extra}_${fDate}_${cleanH}UTC`;
            } else if (product === 'smoke') {
              const smokeLvl = extra || 'surface';
              fHour = `${smokeLvl}_${fDate}_Z${cleanH}`;
            } else if (product === 'aqi') {
              fHour = `aqi_raqdps_${fDate}_Z${cleanH}`;
            }
            const singleUrl = getForecastSingleImageUrl(manifest, product, model, fDate, fHour, res);
            if (singleUrl) {
              preloadSingleWebpWithFallback(singleUrl, product);
            }
          });
          return;
        }

        let effectiveHour = hourStr;
        if (product === 'clouds' && extra) {
          effectiveHour = `${extra}_${dateStr}_${hourStr}UTC`;
        } else if (product === 'smoke') {
          const smokeLvl = extra || 'surface';
          effectiveHour = `${smokeLvl}_${dateStr}_Z${hourStr}`;
        } else if (product === 'aqi') {
          effectiveHour = `aqi_raqdps_${dateStr}_Z${hourStr}`;
        }

        const template = getTileUrl(manifest, product, model, dateStr, effectiveHour);
        if (!template) return;

        if (product === 'winds') {
          const gridUrl = appendAuthToken(template.replace('/{z}/{x}/{y}.webp', '/wind_grid.json'));
          if (!this.preloadedUrls.has(gridUrl)) {
            this.preloadedUrls.add(gridUrl);
            tasks.push(authenticatedFetch(gridUrl).catch(() => {}));
          }
          return;
        }

        const isPmtiles = template.includes('.pmtiles') || template.startsWith('pmtiles://');
        if (isPmtiles) {
          preloadPmtilesArchiveAndViewport(template);
        } else {
          coords.forEach(({ z, x, y }) => {
            const url = template
              .replace('{z}', String(z))
              .replace('{x}', String(x))
              .replace('{y}', String(y));
            if (!this.preloadedUrls.has(url)) {
              this.preloadedUrls.add(url);
              tasks.push(new Promise<void>((resolve) => {
                const img = new Image();
                img.onload = () => resolve();
                img.onerror = () => resolve();
                img.src = url;
              }));
            }
          });
        }
      });
    }

    if (tasks.length === 0) return;

    // Enforce an overall timeout so slow network never stalls animation startup indefinitely
    await Promise.race([
      Promise.allSettled(tasks),
      new Promise(r => setTimeout(r, 3500))
    ]);
  }

  private startMs: number = 0;
  private currentCursorMs: number = 0;

  private startRafLoop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    this.lastFramePerfMs = performance.now();
    this.lastDispatchedHourMs = Math.floor(this.currentCursorMs / (3600 * 1000)) * 3600 * 1000;

    const tick = (now: number) => {
      const currentState = store.getState();
      if (!currentState.forecastAnimationPlaying) {
        this.stop();
        return;
      }

      const dtSec = Math.min(0.1, Math.max(0, (now - this.lastFramePerfMs) / 1000));
      this.lastFramePerfMs = now;

      const durationHours = currentState.forecastAnimationDurationHours || 12;
      const speed = currentState.forecastAnimationSpeed || 2.0;
      const speedMultiplier =
        (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.forecast_animation?.speed_multiplier) || 1.5;
      const stepHours = (typeof __APP_CONFIG__ !== 'undefined' && __APP_CONFIG__?.forecast_animation?.step_hours) || 1;
      const bufferHours =
        (typeof __APP_CONFIG__ !== 'undefined' &&
          (__APP_CONFIG__?.forecast_animation?.lookahead_frames ||
           __APP_CONFIG__?.forecast_animation?.preload_buffer_hours)) ||
        8;

      const endMs = this.startMs + durationHours * 3600 * 1000;

      // Advance virtual timeline continuously (50% faster across 1x, 2x, 4x via speedMultiplier = 1.5)
      this.currentCursorMs += dtSec * speed * speedMultiplier * stepHours * 3600 * 1000;
      if (this.currentCursorMs > endMs || this.currentCursorMs < this.startMs) {
        this.currentCursorMs = this.startMs;
      }

      // 1. Update active WebGL interpolated layers and lightweight scrubber playhead at 60 FPS
      this.frameListeners.forEach(cb => {
        try {
          cb(this.currentCursorMs);
        } catch {}
      });

      // 2. Dispatch integer-hour store updates only when crossing an hour boundary
      const snappedHourMs = Math.floor(this.currentCursorMs / (3600 * 1000)) * 3600 * 1000;
      if (snappedHourMs !== this.lastDispatchedHourMs) {
        this.lastDispatchedHourMs = snappedHourMs;
        store.update({ currentTimeStep: new Date(snappedHourMs) });

        // Directional lookahead preloading along the forward play vector
        const lookaheadDate = new Date(snappedHourMs + Math.max(1, bufferHours - 2) * 3600 * 1000);
        if (lookaheadDate.getTime() <= endMs + 3 * 3600 * 1000) {
          this.preloadHours(lookaheadDate, 4).catch(() => {});
        }
      }

      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }

  /**
   * Updates duration or speed dynamically during active playback without resetting start time.
   */
  public updateLoopSettings(): void {
    const state = store.getState();
    if (!state.forecastAnimationPlaying) return;

    const durationHours = state.forecastAnimationDurationHours || 12;
    const endMs = this.startMs + durationHours * 3600 * 1000;
    if (this.currentCursorMs > endMs || this.currentCursorMs < this.startMs) {
      this.currentCursorMs = this.startMs;
    }
    this.lastFramePerfMs = performance.now();
    if (this.rafId === null) {
      this.startRafLoop();
    }
  }

  /**
   * Initiates forecast playback starting at the current slider position.
   */
  public async start(requestedStartDate?: Date): Promise<void> {
    this.stop(false);

    const state = store.getState();

    // Mutual exclusivity: ensure observed playback loops and pre-buffering are stopped immediately
    const liveWeather = typeof window !== 'undefined' ? (window as any).liveWeatherLayer : null;
    if (liveWeather && typeof liveWeather.stopObservedAnimation === 'function') {
      liveWeather.stopObservedAnimation(false);
    } else if (state.liveWeatherRadarPlaying || state.liveWeatherSatellitePlaying || state.liveWeatherObservedBuffering) {
      store.update({
        liveWeatherRadarPlaying: false,
        liveWeatherSatellitePlaying: false,
        liveWeatherObservedBuffering: false
      });
    }

    const now = new Date();
    const nowRoundedMs = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      0, 0, 0
    )).getTime();

    // Preserve remembered start time if set; otherwise anchor to current step or NOW
    let startMs: number;
    if (requestedStartDate) {
      startMs = requestedStartDate.getTime();
    } else if (state.forecastAnimationStartTime) {
      startMs = state.forecastAnimationStartTime.getTime();
    } else if (state.currentTimeStep) {
      startMs = state.currentTimeStep.getTime();
    } else {
      startMs = nowRoundedMs;
    }

    if (startMs < nowRoundedMs) {
      startMs = nowRoundedMs;
    }
    const startDate = new Date(startMs);
    this.startMs = startMs;
    this.currentCursorMs = (state.currentTimeStep && state.currentTimeStep.getTime() >= startMs)
      ? state.currentTimeStep.getTime()
      : startMs;

    const bufferHours =
      (typeof __APP_CONFIG__ !== 'undefined' &&
        (__APP_CONFIG__?.forecast_animation?.lookahead_frames ||
         __APP_CONFIG__?.forecast_animation?.preload_buffer_hours)) ||
      8;
    const durationHours = state.forecastAnimationDurationHours || 12;
    const initialPreloadHours = Math.min(durationHours + 1, Math.max(bufferHours + 1, 10));

    // Set buffering and playing flags
    store.update({
      activeDockPlaybackMode: 'forecast',
      forecastAnimationPlaying: true,
      forecastAnimationBuffering: true,
      forecastAnimationStartTime: startDate
    });

    // Initial pre-load phase: buffer upcoming hours into ImageBitmapCache before RAF loop starts
    await this.preloadHours(new Date(this.currentCursorMs), initialPreloadHours);

    // If cancelled while buffering, abort
    if (!store.getState().forecastAnimationPlaying) {
      return;
    }

    // Prime GPU textures (tex0 and tex1) from ImageBitmapCache at the starting cursor before Frame 1
    this.frameListeners.forEach(cb => {
      try {
        cb(this.currentCursorMs);
      } catch {}
    });

    store.update({ forecastAnimationBuffering: false });
    this.startRafLoop();
  }

  /**
   * Halts forecast playback and resets buffering flags.
   */
  public stop(updateStore: boolean = true): void {
    const wasRunning = this.rafId !== null || this.timerId !== null;
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    if (updateStore && store.getState().forecastAnimationPlaying) {
      const snappedHourMs = this.currentCursorMs > 0
        ? Math.round(this.currentCursorMs / (3600 * 1000)) * 3600 * 1000
        : null;
      store.update({
        forecastAnimationPlaying: false,
        forecastAnimationBuffering: false,
        ...(snappedHourMs ? { currentTimeStep: new Date(snappedHourMs) } : {})
      });
      if (wasRunning && snappedHourMs) {
        this.frameListeners.forEach(cb => {
          try {
            cb(snappedHourMs);
          } catch {}
        });
      }
    }
  }

  /**
   * Toggles playback between active running and stopped.
   */
  public togglePlay(): void {
    const isPlaying = store.getState().forecastAnimationPlaying;
    if (isPlaying) {
      this.stop();
    } else {
      this.start();
    }
  }

  /**
   * Clears the preloaded tile cache.
   */
  public clearCache(): void {
    this.preloadedUrls.clear();
  }
}

export default ForecastAnimationService;
